import {
  PmfDisappointment,
  PmfQuestionType,
  PmfSurveyFreeTextStep,
  PmfSurveySingleSelectStep,
  PmfSurveyStep,
} from '@griply/types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'shared/assets/icons';
import { AutoSizeTextArea } from 'shared/components/ui/auto-size-text-area';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import {
  IconButton,
  Sizes as IconButtonSizes,
} from 'shared/components/ui/icon-button';

import * as Styled from './pmf-survey-message.style';

type PmfSurveyOptionsProps = {
  step: PmfSurveySingleSelectStep;
  onSelect: (value: PmfDisappointment) => void;
};

const PmfSurveyOptions: React.FC<PmfSurveyOptionsProps> = ({
  step,
  onSelect,
}) => {
  const { t } = useTranslation();

  return (
    <Styled.OptionList>
      {step.options.map((option) => {
        const handleClick = () => onSelect(option.value);
        return (
          <Styled.OptionButton key={option.value} onClick={handleClick}>
            {t(option.labelKey)}
          </Styled.OptionButton>
        );
      })}
    </Styled.OptionList>
  );
};

type PmfSurveyFreeTextProps = {
  step: PmfSurveyFreeTextStep;
  onSubmit: (value: string) => void;
};

const PmfSurveyFreeText: React.FC<PmfSurveyFreeTextProps> = ({
  step,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
  };

  const handleSubmit = () => {
    onSubmit(value);
  };

  return (
    <Styled.Form>
      <Styled.TextAreaWrapper>
        <AutoSizeTextArea
          value={value}
          onChange={handleChange}
          placeholder={step.placeholderKey ? t(step.placeholderKey) : undefined}
          rows={4}
          focusOnMount
        />
      </Styled.TextAreaWrapper>
      <Styled.Actions>
        <Button
          variant={Variants.Contained}
          color={Colors.Primary}
          size={Sizes.Medium}
          onClick={handleSubmit}
        >
          {t('pmf-survey.submit')}
        </Button>
      </Styled.Actions>
    </Styled.Form>
  );
};

export type PmfSurveyMessageProps = {
  step: PmfSurveyStep;
  showThankYou: boolean;
  onSelectOption: (value: PmfDisappointment) => void;
  onSubmitText: (value: string) => void;
  onClose: () => void;
};

export const PmfSurveyMessage: React.FC<PmfSurveyMessageProps> = ({
  step,
  showThankYou,
  onSelectOption,
  onSubmitText,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <Styled.Container>
      <Styled.CloseButtonContainer>
        <IconButton
          icon={X}
          size={IconButtonSizes.Medium}
          aria-label={t('pmf-survey.close')}
          onClick={onClose}
        />
      </Styled.CloseButtonContainer>
      <Styled.Title>
        {showThankYou ? t('pmf-survey.thank-you.title') : t(step.titleKey)}
      </Styled.Title>

      {showThankYou ? (
        <Styled.ThankYou>{t('pmf-survey.thank-you.message')}</Styled.ThankYou>
      ) : step.type === PmfQuestionType.SingleSelect ? (
        <PmfSurveyOptions step={step} onSelect={onSelectOption} />
      ) : (
        <PmfSurveyFreeText key={step.id} step={step} onSubmit={onSubmitText} />
      )}
    </Styled.Container>
  );
};
